// ===============================================================
// ProBank — Push Subscription Helper
//
// Asks the user for notification permission, registers the
// service worker, and saves the phone's push subscription to
// the backend. From then on, the server can drop notifications
// (with sound) on this device — even when the app is closed.
//
// Silent on failure: push problems must never block login.
// ===============================================================

import API from "./api";

// Your PUBLIC VAPID key — this one is safe to ship in the
// frontend (only the private key must stay secret). It must
// match the VAPID_PUBLIC_KEY in your Render environment.
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

// Convert the base64 VAPID key into the format the browser needs.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Register this device for push notifications for the given user.
 * Call it right after a successful login.
 *
 * @param {string} userId — the logged-in user's _id
 * @returns {Promise<boolean>} true if the device is now subscribed
 */
export async function subscribeUserToPush(userId) {
  try {
    // Browser must support service workers + push.
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      console.log("Push not supported on this browser");
      return false;
    }

    if (!VAPID_PUBLIC_KEY) {
      console.warn("REACT_APP_VAPID_PUBLIC_KEY is not set — push disabled");
      return false;
    }

    if (!userId) return false;

    // Don't nag: if the user already blocked notifications, stop.
    if (Notification.permission === "denied") {
      return false;
    }

    // Ask for permission (browser shows the native prompt).
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return false;
    }

    // Register / reuse the service worker.
    const registration = await navigator.serviceWorker.register("/service-worker.js");
    await navigator.serviceWorker.ready;

    // ALWAYS create a fresh subscription with the current key.
    // If an old one exists (with wrong key), unsubscribe it first.
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      await existing.unsubscribe();
      console.log("🔄 Old subscription removed — creating fresh one");
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });

    // Save it on the backend, tagged with this user.
    await API.post("/push/subscribe", {
      userId: String(userId),
      subscription: subscription.toJSON(),
    });

    console.log("✅ Push subscription saved for user", userId);
    return true;
  } catch (err) {
    console.error("Push subscription failed (non-fatal):", err.message || err);
    return false;
  }
}

export default subscribeUserToPush;
